/* ERK size guide & finder (blocks/erk-size-finder.liquid) */
(function () {
  if (customElements.get('erk-size-finder')) return;

  var STORE_KEY = 'erk-size-finder';
  var LIMITS = { weight: [35, 200], height: [140, 220] };

  function toNumber(value) {
    return parseFloat(String(value || '').replace(',', '.'));
  }

  // Fills a translated template such as "Select size __size__"
  function fill(template, values) {
    return String(template || '').replace(/__(\w+)__/g, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function parseList(value) {
    return String(value || '')
      .split(',')
      .map(toNumber)
      .filter(function (n) { return !isNaN(n); })
      .sort(function (a, b) { return a - b; });
  }

  class ErkSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.weightMax = parseList(this.dataset.weightMax);
      this.heightMax = parseList(this.dataset.heightMax);
      var emphasis = toNumber(this.dataset.weightEmphasis);
      this.weightEmphasis = isNaN(emphasis) ? 0.7 : Math.min(Math.max(emphasis, 0), 100) / 100;

      this.dialogs = {
        guide: this.querySelector('[data-dialog="guide"]'),
        finder: this.querySelector('[data-dialog="finder"]')
      };
      this.form = this.querySelector('[data-form]');
      this.result = this.querySelector('[data-result]');
      this.finderTrigger = this.querySelector('[data-finder-trigger]');
      this.finderTriggerText = this.finderTrigger ? this.finderTrigger.textContent.trim() : '';
      this.text = {
        yourSize: this.dataset.tYourSize,
        summary: this.dataset.tSummary,
        select: this.dataset.tSelect,
        unavailable: this.dataset.tUnavailable,
        errorWeight: this.dataset.tErrorWeight,
        errorHeight: this.dataset.tErrorHeight
      };
      this.sizes = Array.prototype.map.call(this.querySelectorAll('[data-size-row]'), function (row) {
        return {
          row: row,
          code: row.dataset.size,
          name: row.dataset.name,
          chest: row.dataset.chest,
          length: row.dataset.length,
          shoulder: row.dataset.shoulder
        };
      });

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('input', this.onInput.bind(this));

      var self = this;
      Object.keys(this.dialogs).forEach(function (key) {
        var dialog = self.dialogs[key];
        dialog.addEventListener('close', function () { self.onDialogClose(); });
        dialog.addEventListener('click', function (event) {
          if (event.target === dialog) dialog.close();
        });
      });

      this.restore();
    }

    /* ---------- Events ---------- */
    onClick(event) {
      var target = event.target;
      var opener = target.closest('[data-open]');
      if (opener) return this.open(opener.dataset.open, opener);

      if (target.closest('[data-close]')) {
        var dialog = target.closest('dialog');
        if (dialog) dialog.close();
        return;
      }

      var switcher = target.closest('[data-switch]');
      if (switcher) return this.open(switcher.dataset.switch, this.lastTrigger);

      var tab = target.closest('[role="tab"]');
      if (tab) return this.selectTab(tab);

      if (target.closest('[data-select]')) return this.applySize();
    }

    onKeydown(event) {
      var tab = event.target.closest('[role="tab"]');
      if (!tab || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
      var tabs = Array.prototype.slice.call(tab.parentElement.querySelectorAll('[role="tab"]'));
      var index = tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
      var next = tabs[(index + tabs.length) % tabs.length];
      this.selectTab(next);
      next.focus();
      event.preventDefault();
    }

    onInput(event) {
      if (event.target.name === 'weight' || event.target.name === 'height') {
        this.setError(event.target.name, '');
      }
    }

    onSubmit(event) {
      event.preventDefault();
      var weight = toNumber(this.form.elements.weight.value);
      var height = toNumber(this.form.elements.height.value);
      var fitInput = this.form.querySelector('input[name="fit"]:checked');
      var fit = fitInput ? fitInput.value : 'regular';

      var weightOk = weight >= LIMITS.weight[0] && weight <= LIMITS.weight[1];
      var heightOk = height >= LIMITS.height[0] && height <= LIMITS.height[1];
      this.setError('weight', weightOk ? '' : this.text.errorWeight);
      this.setError('height', heightOk ? '' : this.text.errorHeight);
      if (!weightOk) return this.form.elements.weight.focus();
      if (!heightOk) return this.form.elements.height.focus();

      var size = this.recommend(weight, height, fit);
      this.state = { weight: weight, height: height, fit: fit, size: size.code };
      this.render(size);
      this.save();
      if (typeof this.result.scrollIntoView === 'function') {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.result.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    /* ---------- Sizing ---------- */
    recommend(weight, height, fit) {
      var weightIndex = this.weightMax.filter(function (limit) { return weight > limit; }).length;
      var heightIndex = this.heightMax.filter(function (limit) { return height > limit; }).length;
      var index = Math.round(this.weightEmphasis * weightIndex + (1 - this.weightEmphasis) * heightIndex);

      if (fit === 'slim') index -= 1;
      if (fit === 'loose') index = Math.max(index + 1, 2); // Loose is always Large or above

      index = Math.min(Math.max(index, 0), this.sizes.length - 1);
      return this.sizes[index];
    }

    /* ---------- Rendering ---------- */
    render(size) {
      var state = this.state;
      this.result.hidden = false;
      this.result.querySelector('[data-result-size]').textContent = size.code;
      this.result.querySelector('[data-result-name]').textContent = size.name;
      this.result.querySelector('[data-result-summary]').textContent = fill(this.text.summary, {
        fit: this.fitLabel(state.fit),
        height: this.format(state.height),
        weight: this.format(state.weight)
      });
      this.result.querySelector('[data-spec="chest"]').textContent = size.chest;
      this.result.querySelector('[data-spec="length"]').textContent = size.length;
      this.result.querySelector('[data-spec="shoulder"]').textContent = size.shoulder;

      Array.prototype.forEach.call(this.result.querySelectorAll('[data-ladder]'), function (step) {
        step.classList.toggle('is-active', step.dataset.ladder === size.code);
      });

      var selectButton = this.result.querySelector('[data-select]');
      var note = this.result.querySelector('[data-select-note]');
      var control = this.findSizeControl(size.code);
      var hasPicker = this.findSizeControl(null) !== null;

      if (selectButton) {
        selectButton.hidden = !control;
        selectButton.textContent = fill(this.text.select, { size: size.code });
      }
      if (note) {
        note.hidden = !(hasPicker && !control);
        note.textContent = hasPicker && !control ? fill(this.text.unavailable, { size: size.code }) : '';
      }

      this.highlightRow(size.code);
      this.updateTrigger(size.code);
    }

    format(value) {
      var rounded = Math.round(value * 10) / 10;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 1 }).format(rounded);
      } catch (error) {
        return String(rounded);
      }
    }

    fitLabel(fit) {
      var title = this.form.querySelector('input[name="fit"][value="' + fit + '"] + .erk-sf__fit-card .erk-sf__fit-title');
      return title ? title.textContent.trim() : fit;
    }

    highlightRow(code) {
      this.sizes.forEach(function (size) {
        size.row.classList.toggle('is-active', !!code && size.code === code);
      });
    }

    updateTrigger(code) {
      if (!this.finderTrigger) return;
      this.finderTrigger.textContent = code ? fill(this.text.yourSize, { size: code }) : this.finderTriggerText;
    }

    setError(name, message) {
      var error = this.form.querySelector('[data-error="' + name + '"]');
      var input = this.form.elements[name];
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      if (input) {
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }

    selectTab(tab) {
      var tabs = tab.parentElement.querySelectorAll('[role="tab"]');
      Array.prototype.forEach.call(tabs, function (item) {
        var selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        var panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }

    /* ---------- Dialogs ---------- */
    open(name, trigger) {
      var dialog = this.dialogs[name];
      if (!dialog) return;
      var self = this;
      Object.keys(this.dialogs).forEach(function (key) {
        if (key !== name && self.dialogs[key].open) {
          self.switching = true;
          self.dialogs[key].close();
          self.switching = false;
        }
      });
      if (trigger) this.lastTrigger = trigger;

      if (name === 'guide') this.highlightRow((this.state && this.state.size) || this.currentSize());

      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
      this.lockScroll(true);

      if (name === 'finder' && !(this.state && this.state.size)) {
        var weight = this.form.elements.weight;
        if (weight && !weight.value) weight.focus();
      }
    }

    onDialogClose() {
      if (this.switching) return;
      var anyOpen = this.dialogs.guide.open || this.dialogs.finder.open;
      if (anyOpen) return;
      this.lockScroll(false);
      var focusTarget = this.returnFocus || this.lastTrigger;
      this.returnFocus = null;
      if (focusTarget && focusTarget.isConnected) focusTarget.focus({ preventScroll: true });
    }

    lockScroll(lock) {
      var root = document.documentElement;
      if (lock) {
        if (this.prevOverflow === undefined) this.prevOverflow = root.style.overflow;
        root.style.overflow = 'hidden';
      } else if (this.prevOverflow !== undefined) {
        root.style.overflow = this.prevOverflow;
        this.prevOverflow = undefined;
      }
    }

    /* ---------- Variant picker ---------- */
    scope() {
      return this.closest('.shopify-section') || this.closest('section') || document;
    }

    isSizeGroup(element) {
      var fieldset = element.closest('fieldset');
      var legend = fieldset && fieldset.querySelector('legend');
      var text = ((legend && legend.textContent) || element.name || '').trim().toLowerCase();
      return text.indexOf('size') === 0 || (element.name || '').toLowerCase().indexOf('size') === 0;
    }

    findSizeControl(code) {
      var scope = this.scope();
      var wanted = code ? code.toUpperCase() : null;
      var radios = Array.prototype.filter.call(
        scope.querySelectorAll('variant-picker input[type="radio"], .variant-picker input[type="radio"]'),
        this.isSizeGroup
      );
      for (var i = 0; i < radios.length; i++) {
        if (!wanted || radios[i].value.trim().toUpperCase() === wanted) return { type: 'radio', element: radios[i] };
      }

      var selects = Array.prototype.filter.call(
        scope.querySelectorAll('variant-picker select, .variant-picker select'),
        this.isSizeGroup
      );
      for (var j = 0; j < selects.length; j++) {
        var options = selects[j].options;
        for (var k = 0; k < options.length; k++) {
          if (!wanted || options[k].value.trim().toUpperCase() === wanted || options[k].text.trim().toUpperCase() === wanted) {
            return { type: 'select', element: selects[j], value: options[k].value };
          }
        }
      }
      return null;
    }

    currentSize() {
      var scope = this.scope();
      var radios = Array.prototype.filter.call(
        scope.querySelectorAll('variant-picker input[type="radio"]:checked, .variant-picker input[type="radio"]:checked'),
        this.isSizeGroup
      );
      if (radios[0]) return radios[0].value.trim().toUpperCase();
      var selects = Array.prototype.filter.call(scope.querySelectorAll('variant-picker select, .variant-picker select'), this.isSizeGroup);
      return selects[0] ? selects[0].value.trim().toUpperCase() : null;
    }

    applySize() {
      if (!this.state) return;
      var control = this.findSizeControl(this.state.size);
      if (!control) return;

      if (control.type === 'radio') {
        if (!control.element.checked) control.element.click();
      } else {
        control.element.value = control.value;
        control.element.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.returnFocus = control.element;
      this.dialogs.finder.close();
    }

    /* ---------- Persistence (this browser session only) ---------- */
    save() {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify(this.state));
      } catch (error) {}
    }

    restore() {
      var saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      } catch (error) {}
      if (!saved || !saved.weight || !saved.height) return;

      this.form.elements.weight.value = saved.weight;
      this.form.elements.height.value = saved.height;
      var fit = this.form.querySelector('input[name="fit"][value="' + saved.fit + '"]');
      if (fit) fit.checked = true;

      var size = this.recommend(saved.weight, saved.height, saved.fit || 'regular');
      this.state = { weight: saved.weight, height: saved.height, fit: saved.fit || 'regular', size: size.code };
      this.render(size);
    }
  }

  customElements.define('erk-size-finder', ErkSizeFinder);
})();